
import { ArrowRight, Loader2 } from 'lucide-react'
import FormField from '@/components/common/FormField'
import { useForm } from 'react-hook-form'
import { errorTypes } from '@/utility/validation'
import { Button } from '@/components/ui/button'
import { Link, useNavigate } from 'react-router-dom'
import AuthLayout from './AuthLayout'
import { useEffect, useState } from 'react'
import { v4 as uuidv4 } from 'uuid';
import type { ILoginData } from '@/interface/auth.model'
import { AuthService } from '@/services/Auth/Auth'
import { setCookies, showToastNotification } from '@/utility/helper'
import { useDispatch } from 'react-redux'
// import { useDispatch } from 'react-redux'
import { setUserData } from '@/store/feature/UserSlice'
import { RoutesLink } from '@/utility/RouteLinks'
import { ThrowError } from '@/api/callApi'
import { useSearchParams } from "react-router-dom";
import { requestToken } from '@/firebase'
import { isSupported } from 'firebase/messaging'


type Inputs = {
    email: string
    password: string,
    deviceId: string
}

const Login: React.FC = () => {
    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<Inputs>({ mode: 'onBlur' })

    const randomDeviceId = uuidv4();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        getMessagingToken();
    }, []);

    const getMessagingToken = async () => {
        try {
            const supported = await isSupported();

            if (!supported) {
                // setIsFcmLoading(false);
                return;
            }

            if (Notification.permission === "granted") {
                await requestToken();
            } else if (Notification.permission === "default") {
                const permission = await Notification.requestPermission();
                if (permission === "granted") {
                    await requestToken();
                } else {
                    localStorage.setItem("fcmToken", "");
                    showToastNotification(
                        "danger",
                        "Please enable notifications to receive updates.",
                    );
                }
            } else {
                localStorage.setItem("fcmToken", "");
                showToastNotification(
                    "danger",
                    "Notifications are blocked. Please enable them in your browser settings to receive updates.",
                );
            }
        } catch (error) {
            ThrowError(error);
        } finally {
            // setIsFcmLoading(false);
        }
    };


    const onSubmit = async (data: ILoginData) => {
        setIsLoading(true);
        try {

            let deviceId = ""
            if (localStorage.getItem(import.meta.env.VITE_BUSINESS + 'deviceId')) {
                deviceId = localStorage.getItem(import.meta.env.VITE_BUSINESS + 'deviceId') ?? '';
            } else {
                deviceId = '2025_' + randomDeviceId;
            }
            localStorage.setItem(import.meta.env.VITE_BUSINESS + 'deviceId', deviceId)

            const payload: ILoginData = {
                email: data.email,
                password: data.password,
                deviceId: deviceId,
                deviceToken: localStorage.getItem("fcmToken") || "",
            }

            if (searchParams.get('client_id')) {
                payload.client_id = searchParams.get('client_id') ?? undefined;
                payload.redirect_uri = searchParams.get('redirect_uri') ?? undefined;
                payload.scope = searchParams.get('scope') ?? undefined;
                payload.state = searchParams.get('state') ?? undefined;
                payload.code_challenge = searchParams.get('code_challenge') ?? undefined;
                payload.code_challenge_method = searchParams.get('code_challenge_method') ?? undefined;
                payload.response_type = searchParams.get('response_type') ?? undefined;
            }

            const response = await AuthService?.login(payload)

            if (response?.status) {
                if (response?.data?.isNavigate) {
                    window.location.href = response?.data?.redirectUrl as string
                    return
                }

                showToastNotification("success", response?.message)
                setCookies(
                    import.meta.env.VITE_BUSINESS + 'token',
                    response?.data?.token?.accessToken as string,
                );
                setCookies(
                    import.meta.env.VITE_BUSINESS + 'refreshToken',
                    response?.data?.token?.refreshToken as string,
                );
                localStorage.setItem(
                    import.meta.env.VITE_BUSINESS + 'token',
                    response?.data?.token?.accessToken as string,
                );
                dispatch(setUserData(response?.data?.user))
                navigate(RoutesLink.superAdminDashboard)
            }
            else {
                showToastNotification("danger", response?.message)
            }
        } catch (error) {
            ThrowError(error)
        }
        finally {
            setIsLoading(false);
        }

    }


    return (
        <AuthLayout>
            <div className="absolute top-0 right-0 -mr-16 -mt-16 w-32 h-32 rounded-full bg-linear-to-br from-primary/20 to-blue-500/20 blur-2xl transition-transform duration-700 group-hover:scale-150"></div>

            <div className="relative z-10 mb-8 text-center">
                <h2 className="text-3xl font-bold text-slate-900 mb-2">
                    Welcome
                </h2>
                <p className="text-slate-500 font-medium">
                    Enter your credentials to access your account
                </p>
            </div>

            <form className="relative z-10 space-y-5" onSubmit={handleSubmit(onSubmit)}>
                <div className="space-y-5">

                    <FormField
                        label="Email Address"
                        placeholder="name@organization.in"
                        type="email"
                        {...register('email', {
                            ...errorTypes.required('Email'),
                            // ...errorTypes.email
                        })}
                        error={errors.email?.message}
                    />
                    <FormField
                        type="password"
                        label="Password"
                        placeholder="••••••••"
                        {...register('password', {
                            ...errorTypes.required('Password'),
                            ...errorTypes.password
                        })}
                        error={errors.password?.message}
                    />
                    <div className='flex justify-end'>
                        <Link to={'/forgot'} className="text-sm font-semibold text-primary hover:text-primary/80 transition-colors">
                            Forgot password?
                        </Link>
                    </div>
                </div>

                <Button
                    type="submit"
                    className="w-full cursor-pointer"
                    disabled={isLoading}
                >
                    {isLoading ? <Loader2 strokeWidth={2} className="w-4 h-4 animate-spin" /> : "Sign In"}
                    {!isLoading && <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover/btn:translate-x-1" />}
                </Button>
            </form>

        </AuthLayout>
    )
}

export default Login